import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import type { PermissionCatalog, RoleBlock } from "@/types/team"
import { IconCheck, IconLock, IconMinus, IconPlus } from "@tabler/icons-react"
import { useMemo } from "react"
import {
  BlockMatch,
  grantBlock,
  matchBlocks,
  revokeBlock,
} from "./permissionResolver"

interface RoleBlockPickerProps {
  catalog: PermissionCatalog
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}

/**
 * Simple-mode role picker: six additive blocks stacked as tappable cards.
 * BASE is always on and spans the full row; A/B/C are role blocks
 * (procurement, compliance officer, sales/QM); D/E are add-ons (master data
 * maintenance, release/accountability).
 *
 * Cards report {matched | modified | empty}: "modified" fires when Advanced
 * edits leave the block partially selected. Only the block's own permissions
 * are checked — BASE's reads inherently overlap A/B/C's closures, so
 * matching against closed dependencies would show every non-BASE block as
 * modified by default (see matchBlock docs).
 */
export function RoleBlockPicker({
  catalog,
  value,
  onChange,
  disabled = false,
}: RoleBlockPickerProps) {
  const blockStatus = useMemo(
    () => matchBlocks(catalog.roleBlocks, value),
    [catalog.roleBlocks, value],
  )

  const toggle = (block: RoleBlock) => {
    const match = blockStatus.get(block.key)
    // Empty → grant; matched or modified → revoke and cascade dependents.
    const shouldGrant = !match || match.status === "empty"
    onChange(
      shouldGrant
        ? grantBlock(block, value, catalog)
        : revokeBlock(block, value, catalog),
    )
  }

  const baseBlock = catalog.roleBlocks.find((b) => b.key === "BASE")
  const nonBase = catalog.roleBlocks.filter((b) => b.key !== "BASE")

  return (
    <div className="space-y-3" role="group">
      {/* BASE stretches full width — it's always on and doesn't compete with the
          toggleable blocks. Visual authority reflects "included by default". */}
      {baseBlock ? (
        <RoleBlockCard
          block={baseBlock}
          match={
            blockStatus.get(baseBlock.key) ?? {
              status: "empty",
              presentCount: 0,
              totalCount: baseBlock.permissions.length,
            }
          }
          onClick={() => toggle(baseBlock)}
          disabled={disabled || baseBlock.locked}
          locked={baseBlock.locked}
          fullWidth
        />
      ) : null}

      {/* Add-on blocks in a 2-col grid on ≥sm; single column on mobile. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {nonBase.map((block) => (
          <RoleBlockCard
            key={block.key}
            block={block}
            match={
              blockStatus.get(block.key) ?? {
                status: "empty",
                presentCount: 0,
                totalCount: block.permissions.length,
              }
            }
            onClick={() => toggle(block)}
            disabled={disabled || block.locked}
            locked={block.locked}
          />
        ))}
      </div>
    </div>
  )
}

// ---- One block card --------------------------------------------------------

interface RoleBlockCardProps {
  block: RoleBlock
  match: BlockMatch
  onClick: () => void
  disabled: boolean
  locked: boolean
  fullWidth?: boolean
}

function RoleBlockCard({
  block,
  match,
  onClick,
  disabled,
  locked,
  fullWidth = false,
}: RoleBlockCardProps) {
  const { translate } = useTranslation()
  // Locked (BASE) always renders as matched — click is a no-op but the visual
  // needs to say "this is included."
  const state = locked ? "matched" : match.status
  const isOn = state === "matched" || state === "modified"

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isOn}
      aria-label={block.label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "group relative flex w-full flex-col gap-3 rounded-2xl border p-5 text-left transition-all duration-150",
        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        // Subtle lift on hover to signal interactivity.
        !disabled &&
          "hover:border-muted-foreground/40 hover:shadow-xs active:scale-[0.995]",
        disabled && !locked && "cursor-not-allowed opacity-70",
        locked && "cursor-default",
        fullWidth && "sm:col-span-2",
        // State-driven border + tint. Warning color for modified, primary for
        // matched, neutral for empty. Shadow only on selected states.
        state === "matched"
          ? "border-primary/70 bg-primary/5 shadow-[0_0_0_1px_rgba(--primary,0.08)]"
          : state === "modified"
            ? "border-warning/70 bg-warning/4"
            : "border-border bg-transparent",
      )}
    >
      {/* Row 1 — key chip + name (left) + indicator (right) */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className={cn(
              "font-mono inline-flex h-6 min-w-[2rem] items-center justify-center rounded-md px-1.5 text-[11px] font-semibold tracking-widest",
              state === "matched"
                ? "bg-primary/15 text-primary"
                : state === "modified"
                  ? "bg-warning/15 text-warning"
                  : "bg-muted text-muted-foreground",
            )}
          >
            {block.key}
          </span>
          <span className="truncate text-base font-semibold leading-tight">
            {block.label}
          </span>
        </div>
        <StateIndicator state={state} locked={locked} />
      </div>

      {/* Row 2 — role hint */}
      <p className="font-mono -mt-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        {block.roleHint}
      </p>

      {/* Row 3 — description */}
      <p className="text-[13px] leading-relaxed text-muted-foreground">
        {block.description}
      </p>

      {/* Row 4 — footer: counter + modified chip */}
      <div className="mt-auto flex items-center justify-between gap-2 border-t border-border/60 pt-3">
        <div className="flex items-center gap-1.5 text-[11px] tabular-nums text-muted-foreground">
          {!locked && state === "empty" ? (
            <>
              <IconPlus size={11} className="text-muted-foreground/70" />
              <span>
                {translate("TEAM/BLOCK_ADDS_PERMISSIONS", {
                  count: block.permissions.length,
                })}
              </span>
            </>
          ) : (
            <span>
              {translate("TEAM/BLOCK_PERMISSION_COUNT", {
                count: match.presentCount,
                total: match.totalCount,
              })}
            </span>
          )}
        </div>
        {state === "modified" ? (
          <span
            className={cn(
              "rounded-full border border-warning/60 bg-warning/10 px-2 py-0.5",
              "font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-warning",
            )}
          >
            {translate("TEAM/BLOCK_MODIFIED")}
          </span>
        ) : null}
      </div>
    </button>
  )
}

// ---- Selection indicator (top-right corner) --------------------------------

function StateIndicator({
  state,
  locked,
}: {
  state: "matched" | "modified" | "empty"
  locked: boolean
}) {
  if (locked) {
    return (
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary bg-primary/15 text-primary"
        aria-hidden
      >
        <IconLock size={12} stroke={2.25} />
      </span>
    )
  }

  if (state === "matched") {
    return (
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary bg-primary text-primary-foreground"
        aria-hidden
      >
        <IconCheck size={13} stroke={3} />
      </span>
    )
  }

  if (state === "modified") {
    // Tri-state indicator: dash renders "some, not all" so the checkbox
    // stops looking mysteriously empty on a modified block.
    return (
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-warning bg-warning/15 text-warning"
        aria-hidden
      >
        <IconMinus size={13} stroke={3} />
      </span>
    )
  }

  return (
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-transparent text-transparent transition-colors group-hover:border-muted-foreground/50"
      aria-hidden
    >
      <IconCheck size={13} stroke={3} />
    </span>
  )
}
